import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom, map, Subject, takeUntil } from 'rxjs';
import { CustomerService } from 'src/app/services/customer.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ConfirmWithTextInputDialogComponent } from 'src/app/shared/confirm-with-text-input-dialog/confirm-with-text-input-dialog.component';
import { ConfirmationDialog } from 'src/app/shared/confirmation-dialog/confirmation.component';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';
import {
  SwitchToLiveDialogComponent,
  SwitchToLiveOptions,
} from './switch-to-live-dialog/switch-to-live-dialog.component';

@Component({
  selector: 'super-admin-demo-settings',
  templateUrl: 'demo-settings.component.html',
  styleUrls: ['demo-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperAdminDemoSettingsComponent {
  i18n = this.translationService.getTranslations('superAdminDemoSettings');
  private destroyed$ = new Subject<void>();

  validUntilControl = new FormControl(null);

  demoValidUntil$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => customer.demoValidUntil),
  );

  remainingDays$ = this.demoValidUntil$.pipe(
    map((validUntil) => {
      if (!validUntil) return null;
      const today = new Date();
      const diffTime = validUntil.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }),
  );

  isExpired$ = this.remainingDays$.pipe(
    map((remainingDays) => remainingDays !== null && remainingDays <= 0),
  );

  customerId$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => customer._id),
  );

  constructor(
    private translationService: TranslationService,
    private userService: UserService,
    private customerService: CustomerService,
    private router: Router,
    private dialog: MatDialog,
    private toastService: ToastService,
  ) {
    this.userService.isAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isAdmin) => {
        if (!isAdmin) {
          this.router.navigate(['/dashboard']);
        }
      });

    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        if (!customer.isDemo) {
          this.router.navigate(['/dashboard']);
        }
        if (customer.demoValidUntil) {
          this.validUntilControl.setValue(customer.demoValidUntil);
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  async onSaveExtendDuration() {
    const newDate = this.validUntilControl.value;
    if (!newDate) return;

    const customerId = await firstValueFrom(this.customerId$);

    Meteor.call(
      'setDemoValidUntil',
      { customerId, newValidUntil: newDate },
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: this.i18n.errorExtendingDuration,
          });
        } else {
          this.toastService.add({
            type: 'SUCCESS',
            message: this.i18n.durationExtended,
          });
          this.validUntilControl.markAsPristine();
        }
      },
    );
  }

  async disableDemo() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: 'disableDemo',
        title: this.i18n.disableDemoTitle,
        text: this.i18n.disableDemoMessage,
        confirmText: this.i18n.disableDemo,
        cancelText: this.i18n.cancel,
      },
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    console.log('confirmed', confirmed);
    if (!confirmed) return;

    const customerId = await firstValueFrom(this.customerId$);
    const today = new Date();

    Meteor.call(
      'setDemoValidUntil',
      { customerId, newValidUntil: today },
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: this.i18n.errorDisablingDemo,
          });
        } else {
          this.toastService.add({
            type: 'SUCCESS',
            message: this.i18n.demoDisabled,
          });
        }
      },
    );
  }

  async deleteDemo() {
    const customer = await firstValueFrom(
      this.customerService.readyDispoCustomer$,
    );

    const dialogRef = this.dialog.open(ConfirmWithTextInputDialogComponent, {
      data: {
        title: this.i18n.deleteDemoTitle,
        message: this.i18n.deleteDemoMessage?.replace(
          '{{name}}',
          customer.name,
        ),
        expectedText: customer.name,
        inputLabel: this.i18n.customerName || 'Kundenname',
        confirmText: this.i18n.deleteDemo,
        cancelText: this.i18n.cancel,
      },
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) return;

    const customerId = customer._id;

    Meteor.call('deleteDemo', { customerId }, (error: Meteor.Error) => {
      if (error) {
        this.toastService.add({
          type: 'ERROR',
          message: this.i18n.errorDeletingDemo,
        });
      } else {
        this.toastService.add({
          type: 'SUCCESS',
          message: this.i18n.demoDeleted,
        });
        setTimeout(() => {
          this.router.navigate(['/dashboard']).then(() => {
            window.location.reload();
          });
        }, 300);
      }
    });
  }

  async switchToLive() {
    const customer = await firstValueFrom(
      this.customerService.readyDispoCustomer$,
    );

    // First, show the options dialog
    const optionsDialogRef = this.dialog.open(SwitchToLiveDialogComponent, {
      data: {},
    });

    const options = (await firstValueFrom(
      optionsDialogRef.afterClosed(),
    )) as SwitchToLiveOptions | null;
    if (!options) return;

    // Then, show the confirmation dialog with text input
    const confirmDialogRef = this.dialog.open(
      ConfirmWithTextInputDialogComponent,
      {
        data: {
          title: this.i18n.confirmSwitchToLiveTitle,
          message: this.i18n.confirmSwitchToLiveMessage?.replace(
            '{{name}}',
            customer.name,
          ),
          expectedText: customer.name,
          inputLabel: this.i18n.customerName || 'Kundenname',
          confirmText: this.i18n.switchToLive,
          cancelText: this.i18n.cancel,
        },
      },
    );

    const confirmed = await firstValueFrom(confirmDialogRef.afterClosed());
    if (!confirmed) return;

    const customerId = customer._id;

    Meteor.call(
      'switchDemoToLive',
      { customerId, options },
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: this.i18n.errorSwitchingToLive,
          });
        } else {
          this.toastService.add({
            type: 'SUCCESS',
            message: this.i18n.switchedToLive,
          });
          this.router.navigate(['/dashboard']);
        }
      },
    );
  }
}
